// ============================================================
// BansPage — MEN-239 [V1-11]
// Bannissement instance : créer un ban (permanent ou temporaire),
// lister les bans actifs, lever un ban. Le bannissement déconnecte
// l'utilisateur en temps réel + invalide son accessToken (serveur).
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { AdminBan } from '@siverix/api-client';
import type { AuditLogLive } from '@siverix/shared';
import { ErrorBox } from './OverviewPage';
import { useSocket } from '../hooks/useSocket';

/** [2.10.0] Actions du journal d'audit qui changent la liste des bannissements. */
const BAN_ACTIONS = new Set(['user.ban', 'user.unban']);

interface UserOption {
  id: string;
  username: string;
}

export function BansPage(): JSX.Element {
  const [rows, setRows] = useState<AdminBan[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Formulaire de création
  const [userSearch, setUserSearch] = useState('');
  const [userId, setUserId] = useState('');
  const [userOpts, setUserOpts] = useState<UserOption[]>([]);
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { socket } = useSocket();

  // [2.10.0] `quiet` : relecture en direct, sans écran « Chargement… » et sans
  // effacer un message d'erreur affiché.
  const fetchRows = useCallback(async (quiet = false): Promise<void> => {
    if (!quiet) {
      setLoading(true);
      setErr(null);
    }
    try {
      setRows(await api.adminListBans());
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  // [2.10.0] Ban posé ou levé par un autre admin (ou un autre onglet) : la
  // liste se relit d'elle-même (avant : seulement à l'ouverture de la page).
  useEffect(() => {
    if (!socket) return;
    const onAudit = (entry: AuditLogLive): void => {
      if (BAN_ACTIONS.has(entry.action)) void fetchRows(true);
    };
    socket.on('audit:new', onAudit);
    return () => {
      socket.off('audit:new', onAudit);
    };
  }, [socket, fetchRows]);

  // Autocomplete utilisateur (recherche serveur best-effort).
  useEffect(() => {
    let cancelled = false;
    const q = userSearch.trim();
    if (q.length < 2) {
      setUserOpts([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .adminListUsers(q)
        .then((us) => {
          if (!cancelled) setUserOpts(us.map((u) => ({ id: u.id, username: u.username })));
        })
        .catch(() => {
          if (!cancelled) setUserOpts([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [userSearch]);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!userId || !reason.trim()) {
      setErr('Sélectionnez un utilisateur et indiquez un motif.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await api.adminCreateBan({
        userId,
        reason: reason.trim(),
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined,
      });
      setUserSearch('');
      setUserId('');
      setReason('');
      setExpiresAt('');
      await fetchRows();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function unban(uid: string): Promise<void> {
    try {
      await api.adminDeleteBan(uid);
      await fetchRows();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Bannissements</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Un ban déconnecte immédiatement l'utilisateur (temps réel) et invalide son jeton d'accès. Un
        ban peut être permanent ou expirer à une date donnée.
      </p>

      {err && <ErrorBox message={err} />}

      <form
        onSubmit={(e) => void submit(e)}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
          marginBottom: '18px',
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          padding: '14px',
        }}
      >
        <label style={lbl}>
          Utilisateur
          <input
            list="ban-users"
            placeholder="username…"
            value={userSearch}
            onChange={(e) => {
              setUserSearch(e.target.value);
              const m = userOpts.find((o) => o.username === e.target.value);
              setUserId(m ? m.id : '');
            }}
            style={inp}
          />
          <datalist id="ban-users">
            {userOpts.map((o) => (
              <option key={o.id} value={o.username} />
            ))}
          </datalist>
        </label>
        <label style={{ ...lbl, flex: 1, minWidth: 220 }}>
          Motif
          <input
            placeholder="raison du ban…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={inp}
          />
        </label>
        <label style={lbl}>
          Expire le (optionnel)
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            style={inp}
          />
        </label>
        <button type="submit" disabled={submitting} style={btnDanger}>
          {submitting ? 'Bannissement…' : 'Bannir'}
        </button>
      </form>

      {loading ? (
        <div>Chargement…</div>
      ) : (
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
                <th style={th}>Utilisateur</th>
                <th style={th}>Motif</th>
                <th style={th}>Par</th>
                <th style={th}>Le</th>
                <th style={th}>Expire</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.userId} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={td}>{b.user?.username ?? b.userId}</td>
                  <td style={{ ...td, maxWidth: 320, whiteSpace: 'pre-wrap' }}>{b.reason}</td>
                  <td style={td}>{b.bannedBy?.username ?? b.bannedById}</td>
                  <td style={td}>{new Date(b.bannedAt).toLocaleString('fr-FR')}</td>
                  <td style={td}>
                    {b.expiresAt ? new Date(b.expiresAt).toLocaleString('fr-FR') : 'Permanent'}
                  </td>
                  <td style={td}>
                    <button onClick={() => void unban(b.userId)} style={btnGhost} type="button">
                      Lever
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      ...td,
                      color: 'var(--color-text-muted)',
                      textAlign: 'center',
                      padding: '24px',
                    }}
                  >
                    Aucun bannissement actif.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '10px 16px',
  fontWeight: 600,
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const td: React.CSSProperties = { padding: '8px 16px' };
const lbl: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  fontSize: '11px',
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
};
const inp: React.CSSProperties = {
  padding: '6px 8px',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  color: 'var(--color-text)',
  fontSize: '13px',
};
const btnDanger: React.CSSProperties = {
  padding: '7px 14px',
  background: 'var(--color-error, #f85149)',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '5px 10px',
  background: 'transparent',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '12px',
  cursor: 'pointer',
};
